import { useTranslation } from 'react-i18next';
import { formatPoints } from '@kidshome/i18n';
import type { CosmeticCatalogItemResponse } from '@kidshome/api-contract';
import { cn } from '@kidshome/ui';
import { tDynamic } from '@/shared/lib/i18n';
import { useHouseholdContext } from '@/shared/lib/household';
import { CosmeticPreview } from '@/widgets/cosmetics-shop/CosmeticPreview';
import { CheckGlyph } from '@/widgets/cosmetics-shop/wardrobe-glyphs';
import { CurrencyGlyph } from '@/entities/hero';

/**
 * One cosmetic ART-FIRST TILE (ADR-0098 wardrobe · ADR-0101 effort-stars): a square the SIZE of a kid touch
 * target whose face IS the item's art ({@link CosmeticPreview}) — the tile itself is the button, so a
 * non-reader picks by picture. Corners carry the state (game-inventory convention, center stays art): a
 * bottom-corner ✨ price pill (the look's ✨ cost — ADR-0103), a top-corner worn tick. There is no level
 * PRICE gate (a look may still need a hero LEVEL, ADR-0103); unlocking is "save ✨ toward it", never a padlock.
 * One tap:
 *
 *   - EQUIPPED → selection ring + a filled CHECK badge; tap toggles it OFF (back to the default look).
 *   - OWNED, not worn → NO badge (only the WORN look is checked); a faint hairline marks it "yours".
 *     Tap EQUIPS instantly (free, reversible).
 *   - UNOWNED, affordable → ✨ price pill (highlighted "ready"); tap opens a kind try-on-then-confirm
 *     sheet (never a one-tap spend — ADR-0029).
 *   - UNOWNED, not yet affordable → SAME ✨ pill + a soft dim + a thin SAVE METER (how close their ✨ is);
 *     the accessible name announces the cost; tap opens the same sheet framed as a save goal (no Unlock button).
 *
 * State is shown by badge + SHAPE + dim, never colour alone (colour-blind a11y).
 */
export function CosmeticTile({
  item,
  stars,
  onEquipToggle,
  onBuyIntent,
  busy = false,
}: {
  item: CosmeticCatalogItemResponse;
  /** The child's spendable ✨ — to show how close a not-yet-affordable look's save meter is. */
  stars: number;
  onEquipToggle: (itemId: string, currentlyEquipped: boolean) => void;
  /** Opens the confirm/save sheet for any UNOWNED look (affordable OR still saving). */
  onBuyIntent: (item: CosmeticCatalogItemResponse) => void;
  busy?: boolean;
}): React.ReactElement {
  const { t } = useTranslation('family');
  const { locale } = useHouseholdContext();
  const itemName = tDynamic(t, `common:cosmetics.item.${item.itemId}.name`);
  const owned = item.owned;
  const equipped = item.equipped;
  // The look's ✨ price — now that ✨ is a concrete, named currency (ADR-0103) the kid sees the real cost
  // (e.g. "150") right against their balance, not the old "≈ N days" abstraction.
  const priceLabel = formatPoints(locale, item.priceStars);
  const savePct =
    item.priceStars > 0 ? Math.min(100, Math.round((stars / item.priceStars) * 100)) : 100;

  // The accessible name carries the full state a sighted kid reads from the badge/pill/dim: worn, owned,
  // ready to unlock, or still saving (the cost in ✨). No level, no lock — every look is reachable.
  const stateWord = equipped
    ? t('shop.equipped')
    : owned
      ? t('shop.owned')
      : item.affordable
        ? t('shop.ready')
        : t('shop.starsLabel', { count: item.priceStars });

  const activate = (): void => {
    // Owned → equip in place. Everything UNOWNED opens the sheet (affordable → confirm; saving → goal view).
    if (owned) onEquipToggle(item.itemId, equipped);
    else onBuyIntent(item);
  };

  return (
    <div className="tile-wrap">
      <button
        type="button"
        className={cn(
          'tile',
          equipped && 'is-equipped',
          owned && !equipped && 'is-owned',
          !owned && !item.affordable && 'is-poor',
        )}
        aria-pressed={equipped}
        aria-label={`${itemName} — ${stateWord}`}
        disabled={busy}
        onClick={activate}
      >
        <span className="tile-art" aria-hidden="true">
          <CosmeticPreview category={item.place} itemId={item.itemId} />
        </span>

        {/* top-corner badge — ONLY the worn look is checked (so the grid never shows several "selected"
            ticks); an owned-but-unworn look carries no badge. */}
        {equipped ? (
          <span className="tile-badge on" aria-hidden="true">
            <CheckGlyph />
          </span>
        ) : null}

        {/* bottom-corner ✨ pill — the look's ✨ price (ADR-0103); "ready" once the kid holds enough ✨. */}
        {!owned && (
          <span className={cn('tile-price star', item.affordable && 'ready')} aria-hidden="true">
            <CurrencyGlyph />
            {priceLabel}
          </span>
        )}

        {/* a thin SAVE METER under a still-saving look — a gentle "you're this close", never shaming. */}
        {!owned && !item.affordable && (
          <span className="tile-save" aria-hidden="true">
            <span className="tile-save-fill" style={{ width: `${savePct}%` }} />
          </span>
        )}
      </button>
      <span className="tile-name" aria-hidden="true">
        {itemName}
      </span>
    </div>
  );
}
